<template>
  <PageHeader
    breadcrumb
    :items="[{ label: $t('ui.entities.setting', 2), to: '/settings' }]"
  />

  <Tabs
    v-model="currentTab"
    :tabs="tabs"
    type="pills-rail"
    size="xs"
    class="mb-4"
  />

  <Card v-if="currentTab === 'users'">
    <TableBasic>
      <thead>
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Name</th>
          <th>Active Call Center</th>
          <th>Sockets</th>
          <th>Presence</th>
          <th>Agent Status</th>
          <th>Last Active</th>
          <th></th>
        </tr>
      </thead>
      <tr
        v-for="user in users"
        :key="user.id"
      >
        <td>{{ user.id }}</td>
        <td>{{ user.typeName }}</td>
        <td>{{ user.name }}</td>
        <td>{{ user.activeCallCenterName }}</td>
        <td>
          <Chip
            v-for="socket in user.sockets"
            :key="socket.id"
            :label="socket"
            size="xs"
          />
        </td>
        <td>
          <Select
            v-model="user.status"
            :options="[
              { value: 'online', label: 'Online', color: 'success' },
              { value: 'idle', label: 'Idle', color: 'warning' },
              { value: 'offline', label: 'Offline', color: 'default' }
            ]"
            @select="updatePresence(user, $event)"
          >
            <template #value="{ value }">
              <div class="flex items-center gap-1">
                <Icon
                  type="circle"
                  fa-style="fas"
                  class="text-xs mt-1"
                  :class="['fg-' + value.color]"
                />
                <div>{{ value.label }}</div>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex items-center gap-1">
                <Icon
                  type="circle"
                  fa-style="fas"
                  class="text-xs mt-1"
                  :class="['fg-' + option.color]"
                />
                <div>{{ option.label }}</div>
              </div>
            </template>
          </Select>
        </td>
        <td>
          <Select
            v-model="user.agentStatus"
            :options="[
              { value: 'available', label: 'Available', color: 'success' },
              { value: 'on-call', label: 'On Call', color: 'success' },
              { value: 'break', label: 'Break', color: 'warning' },
              { value: 'offline', label: 'Offline', color: 'default' }
            ]"
            @select="updateAgentStatus(user, $event)"
          >
            <template #value="{ value }">
              <div class="flex items-center gap-1">
                <Icon
                  type="circle"
                  fa-style="fas"
                  class="text-xs mt-1"
                  :class="['fg-' + value.color]"
                />
                <div>{{ value.label }}</div>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex items-center gap-1">
                <Icon
                  type="circle"
                  fa-style="fas"
                  class="text-xs mt-1"
                  :class="['fg-' + option.color]"
                />
                <div>{{ option.label }}</div>
              </div>
            </template>
          </Select>
        </td>
        <td>{{ dayjs(user.lastActive).fromNow() }}</td>
        <td>
          <Dropdown>
            <template #triggerContent>
              <Button
                icon="ellipsis-vertical"
                plain
                severity="secondary"
              ></Button>
            </template>
            <DropdownItem @click="alertToast('Edit')">
              <div>
                <Icon
                  type="pencil"
                  fa-style="fas"
                  class="mr-2"
                />
                Reset Status
              </div>
            </DropdownItem>
          </Dropdown>
        </td>
      </tr>
    </TableBasic>
  </Card>

  <Card v-if="currentTab === 'conferences'">
    <TableBasic>
      <thead>
        <tr>
          <th>SID</th>
          <th>From</th>
          <th>To</th>
          <th>Initiator</th>
          <th>Agent ID</th>
          <th>Started</th>
          <th>Ended</th>
          <th>Length</th>
          <th>Status</th>
        </tr>
      </thead>
      <tr
        v-for="conference in conferences"
        :key="conference.sid"
      >
        <td>{{ conference.sid }}</td>
        <td>{{ conference.from }}</td>
        <td>{{ conference.to }}</td>
        <td>{{ conference.initiatedByEntityType }}</td>
        <td>{{ conference.initiatedByEntityId }}</td>
        <td>{{ dayjs(conference.startedAt).fromNow() }}</td>
        <td>{{ conference.endedAt ? dayjs(conference.endedAt).fromNow() : 'Active' }}</td>
        <td>{{ conference.duration }}</td>
        <td>
          <Chip
            :label="capitalize(conference.status)"
            size="xs"
          />
        </td>
      </tr>
    </TableBasic>
  </Card>

  <Card v-if="currentTab === 'agent-calls'">
    <TableBasic>
      <thead>
        <tr>
          <th>Agent Name</th>
          <th>Conferences</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in users"
          :key="agent.id"
        >
          <td>{{ agent.name }}</td>
          <td>
            <template v-if="agent.conferences && agent.conferences.length">
              <div
                v-for="conference in agent.conferences"
                :key="conference.sid"
                class="bg-tertiary p-2 rounded-lg inline-flex flex-col"
              >
                <div>{{ conference.from }} -> {{ conference.to }}</div>
                <div>{{ conference.status }}</div>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </TableBasic>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'

import { capitalize } from 'lodash'

import { useCallCenterStore } from '@/stores/CallCenterStore'
import { useSocketStore } from '@/stores/SocketStore'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import TableBasic from '@/components/tables/TableBasic.vue'
import Tabs from '@/components/tabs/Tabs.vue'
import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'

import Select from '@/components/forms/Select.vue'

import { alertToast } from '@/utilities/notification'

const callCenterStore = useCallCenterStore()
const socketStore = useSocketStore()

const tabs = ref([
  { id: 'users', label: 'Users' },
  { id: 'conferences', label: 'Conferences' },
  { id: 'agent-calls', label: 'Agent Calls' }
])
const currentTab = ref(tabs.value[0].id)

const users = computed(() => {
  // Loop through each user in the list, and assign the active call center name
  const socketStoreUsers = socketStore.activeUsers.map(user => {
    user.activeCallCenterName = callCenterStore.callCenters.find(callCenter => callCenter.id === user.activeCallCenter)?.name
    user.typeName = user.type === 1 ? 'Internal' : 'External'

    user.conferences = socketStore.callCenterConferences.filter(conference => conference.initiatedByEntityId === user.id)
    return user
  })

  return socketStoreUsers
})

const conferences = computed(() => {
  return socketStore.callCenterConferences
})

function updatePresence(user, presence) {
  socketStore.setUserPresence(presence, user.id)
}

function updateAgentStatus(user, agentStatus) {
  socketStore.updateAgentStatus(agentStatus, user.id)
}
</script>
